import React, { useState, useEffect } from "react";
import "../../styles/assumptionPopup.css";
import { X, Plus } from "lucide-react";
import { toast } from "react-toastify";
import axiosInstance from "../../axiosInstance";

const AssumptionsPopup = ({ closePopup, editData,triggerReload }) => {
  const [title, setTitle] = useState('');
  const [effectiveFromDate, setEffectiveFromDate] = useState('');
  const [effectiveToDate, setEffectiveToDate] = useState('');
  const [rows, setRows] = useState([{ id: null, label: '', description: '' }]);
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    console.log("Edit data received:", editData);
    
    if (editData?.id) {
      const fetchData = async () => {
        try {
          const response = await axiosInstance.get(
            `/costsolution/assumptions/get/${editData.id}`
          );
          console.log("API Response:", response.data);

          // Set basic fields
          setTitle(response.data.assumption?.title || "");
          setEffectiveFromDate(response.data.assumption?.effective_from_date || "");
          setEffectiveToDate(response.data.assumption?.effective_to_date || "");

          // Handle the assumptionClone data
          const assumptionData = response.data.assumption;
          if (assumptionData?.assumptionClone?.length > 0) {
            setRows(assumptionData.assumptionClone.map(item => ({
              id: item.assumptionCloneId,
              label: item.label || '',
              description: item.description || ''
            })));
          } else {
            setRows([{ id: null, label: '', description: '' }]);
          }

          setIsEditing(true);

        } catch (error) {
          console.error("Error fetching data:", error);
          toast.error("Error fetching assumption details");
        }
      };
      fetchData();
    } else {
      // Reset for new entry
      setTitle('');
      setEffectiveFromDate('');
      setEffectiveToDate('');
      setRows([{ id: null, label: '', description: '' }]);
      setIsEditing(false);
    }
  }, [editData]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Prepare the data to send - matches your save API structure
    const postData = {
      title,
      effectiveFromDate,
      effectiveToDate,
      assumptionsClones: rows
        .filter(row => row.label.trim() || row.description.trim())
        .map(row => ({
          id: row.id || "",
          label: row.label,
          description: row.description
        }))
    };

    console.log("Submitting data:", postData);

    try {
      const url = isEditing
        ? `/costsolution/assumptions/${editData?.id}`
        : "/costsolution/assumptions";

      const method = isEditing ? "put" : "post";

      const response = await axiosInstance({
        method,
        url,
        data: postData
      });


      console.log("API Response:", response.data);

      if (response.status === 200 || response.status === 201) {
        toast.success(isEditing ? "Assumption updated successfully!" : "Assumption created successfully!");
        triggerReload(); 
        closePopup();
      } else {
        toast.error(response.data.message || "Failed to save assumption");
      }
    } catch (error) {
      console.error("Error:", error);
      toast.error(error.response?.data?.message || "Something went wrong!");
    }
  };

  const handleClear = () => {
    setTitle('');
    setEffectiveFromDate('');
    setEffectiveToDate('');
    setRows([{ id: null, label: '', description: '' }]);
  };

  const addRow = () => {
    setRows([...rows, { id: null, label: '', description: '' }]);
  };

  const removeRow = (index) => {
    const newRows = [...rows];
    newRows.splice(index, 1);
    setRows(newRows.length > 0 ? newRows : [{ id: null, label: '', description: '' }]);
  };

  const handleRowChange = (index, field, value) => {
    const newRows = [...rows];
    newRows[index][field] = value;
    setRows(newRows);
  };

  return (
    <div className="modal-overlay">
      <div className="modal-container">
        <div className="modal-header">
          <h2>{isEditing ? 'Edit Assumption' : 'Create Assumption'}</h2>
          <button className="close-button" onClick={closePopup}>X</button>
        </div>
        
        <div className="modal-contents">
          <div className="form-group">
            <label className="assumption-title">Title</label>
            <input
              type=""
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Enter title"
            />
          </div>

          <div className="form-group">
            <label className="assumption-effective-from">Effective From</label>
            <input
              type="date"
              value={effectiveFromDate}
              onChange={(e) => setEffectiveFromDate(e.target.value)}
            />
          </div>

          <div className="form-group">
            <label className="assumption-effective-to">Effective To</label>
            <input
              type="date"
              value={effectiveToDate}
              onChange={(e) => setEffectiveToDate(e.target.value)}
            />
          </div>

          <div className="assumption-table-container">
            <div className="table-wrapper">
              <table className="assumption-table">
                <thead className="assumption-thead">
                  <tr className="assumption-tr">
                    <th className="assumption-th label-column">LABEL</th>
                    <th className="assumption-th description-column">DESCRIPTION</th>
                    <th className="assumption-th action-column"></th>
                  </tr>
                </thead>

                <tbody className="assumption-tbody">
                  {rows.map((row, index) => (
                    <tr className="assumption-tr" key={index}>
                      <td className="assumption-td label-column">
                        <input
                          type=""
                          value={row.label}
                          onChange={(e) => handleRowChange(index, 'label', e.target.value)}
                          placeholder="Enter label"
                          className="table-input"
                        />
                      </td>
                      <td className="assumption-td description-cell">
                        <input
                          type=""
                          value={row.description}
                          onChange={(e) => handleRowChange(index, 'description', e.target.value)}
                          placeholder="Enter description"
                          className="table-input"
                        />
                        {rows.length > 1 && (
                          <button 
                            className="remove-row-button" 
                            onClick={() => removeRow(index)}
                            type="button"
                          >
                            <X size={16} />
                          </button>
                        )}
                      </td>
                      <td></td>
                    </tr>
                  ))}
                </tbody>

                <tfoot>
              <tr>
                <td className="assumption-td label-column"></td>
                <td></td>
                <td style={{ textAlign: "right" }}>
                  <button className="add-button" onClick={addRow}>
                    <Plus size={18} />
                  </button>
                </td>
              </tr>
            </tfoot>
              </table>
            </div>
          </div>

          <div className="modal-footer">
            <button className="clear-button" onClick={handleClear} type="button">
              Clear
            </button>
            <button className="submit-button" onClick={handleSubmit}>
              {isEditing ? 'Update' : 'Submit'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AssumptionsPopup
